import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Calendar, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { useTechs } from '@/hooks/useReferenceData';
import { useOverrideRules, useCreateOverrideRule, useUpdateOverrideRule, useDeleteOverrideRule, type OverrideRule, type NewOverrideRule } from '@/hooks/useCommissionOverrides';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useAuth } from '@/hooks/useAuth';

const overrideRuleSchema = z.object({
  source_technician_id: z.string().uuid('Please select a source technician'),
  beneficiary_technician_id: z.string().uuid('Please select a beneficiary technician'),
  rate_percentage: z.coerce.number().min(0, 'Rate must be at least 0%').max(100, 'Rate cannot exceed 100%'),
  effective_from: z.string().min(1, 'Effective from date is required'),
  effective_to: z.string().optional().nullable(),
}).refine(
  (data) => data.source_technician_id !== data.beneficiary_technician_id,
  { message: 'Cannot create override to self', path: ['beneficiary_technician_id'] }
).refine(
  (data) => {
    if (!data.effective_to) return true;
    return new Date(data.effective_to) >= new Date(data.effective_from);
  },
  { message: 'End date must be on or after start date', path: ['effective_to'] }
);

type OverrideRuleFormValues = z.infer<typeof overrideRuleSchema>;

export default function TechOverrides() {
  const { tenantId } = useTenantContext();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<OverrideRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<boolean | undefined>(true);

  const { data: techs = [] } = useTechs(true);
  const { data: overrideRules = [], isLoading } = useOverrideRules({ isActive: filterActive });
  const createRule = useCreateOverrideRule();
  const updateRule = useUpdateOverrideRule();
  const deleteRule = useDeleteOverrideRule();

  const form = useForm<OverrideRuleFormValues>({
    resolver: zodResolver(overrideRuleSchema),
    defaultValues: {
      source_technician_id: '',
      beneficiary_technician_id: '',
      rate_percentage: 0,
      effective_from: format(new Date(), 'yyyy-MM-dd'),
      effective_to: null,
    }
  });

  const handleOpenDialog = (rule?: OverrideRule) => {
    if (rule) {
      setEditingRule(rule);
      form.reset({
        source_technician_id: rule.source_technician_id,
        beneficiary_technician_id: rule.beneficiary_technician_id,
        rate_percentage: Number(rule.rate_percentage),
        effective_from: rule.effective_from,
        effective_to: rule.effective_to || null,
      });
    } else {
      setEditingRule(null);
      form.reset({
        source_technician_id: '',
        beneficiary_technician_id: '',
        rate_percentage: 0,
        effective_from: format(new Date(), 'yyyy-MM-dd'),
        effective_to: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
    form.reset();
  };

  const onSubmit = async (values: OverrideRuleFormValues) => {
    if (!tenantId || !user?.id) return;

    try {
      if (editingRule) {
        await updateRule.mutateAsync({
          id: editingRule.id,
          updates: {
            source_technician_id: values.source_technician_id,
            beneficiary_technician_id: values.beneficiary_technician_id,
            rate_percentage: values.rate_percentage,
            effective_from: values.effective_from,
            effective_to: values.effective_to || null,
          }
        });
      } else {
        const newRule: NewOverrideRule = {
          company_id: tenantId,
          source_technician_id: values.source_technician_id,
          beneficiary_technician_id: values.beneficiary_technician_id,
          rate_percentage: values.rate_percentage,
          effective_from: values.effective_from,
          effective_to: values.effective_to || null,
          created_by: user.id,
        };
        await createRule.mutateAsync(newRule);
      }
      handleCloseDialog();
    } catch (error) {
      // Error handling is done in the mutation hooks
    }
  };

  const handleEndDate = async (rule: OverrideRule) => {
    await updateRule.mutateAsync({
      id: rule.id,
      updates: {
        effective_to: format(new Date(), 'yyyy-MM-dd'),
      }
    });
  };

  const handleDelete = async () => {
    if (ruleToDelete) {
      await deleteRule.mutateAsync(ruleToDelete);
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const openDeleteDialog = (ruleId: string) => {
    setRuleToDelete(ruleId);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Tech Commission Overrides</CardTitle>
              <CardDescription>
                Configure commission sharing rules where beneficiary technicians earn a percentage of source technicians' net revenue
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Override Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Show:</span>
              <Button
                variant={filterActive === true ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterActive(true)}
              >
                Active
              </Button>
              <Button
                variant={filterActive === false ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterActive(false)}
              >
                Inactive
              </Button>
              <Button
                variant={filterActive === undefined ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterActive(undefined)}
              >
                All
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading override rules...</div>
          ) : overrideRules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No override rules found. Create one to get started.
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source Tech</TableHead>
                    <TableHead>Beneficiary Tech</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Effective From</TableHead>
                    <TableHead>Effective To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overrideRules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-medium">
                        {rule.source_tech?.name || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {rule.beneficiary_tech?.name || 'Unknown'}
                      </TableCell>
                      <TableCell>{Number(rule.rate_percentage).toFixed(2)}%</TableCell>
                      <TableCell>{format(new Date(rule.effective_from), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        {rule.effective_to ? format(new Date(rule.effective_to), 'MMM dd, yyyy') : 'Ongoing'}
                      </TableCell>
                      <TableCell>
                        {rule.is_active ? (
                          <Badge variant="default">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(rule)}
                            title="Edit override rule"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {rule.is_active && !rule.effective_to && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEndDate(rule)}
                              title="End this rule today"
                            >
                              <Calendar className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(rule.id)}
                            title="Delete override rule"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Override Rule' : 'Create Override Rule'}</DialogTitle>
            <DialogDescription>
              Configure commission sharing between technicians. The beneficiary will earn a percentage of the source tech's net revenue.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="source_technician_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source Technician</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source tech" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {techs.map((tech) => (
                          <SelectItem key={tech.id} value={tech.id}>
                            {tech.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>The tech who generates the revenue</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="beneficiary_technician_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beneficiary Technician</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select beneficiary tech" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {techs
                          .filter((tech) => tech.id !== form.watch('source_technician_id'))
                          .map((tech) => (
                            <SelectItem key={tech.id} value={tech.id}>
                              {tech.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>The tech who earns the override commission</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rate_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Override Rate (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="2.00"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Percentage of net revenue (0-100)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="effective_from"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective From</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription>Date when this rule becomes active</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="effective_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective To (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormDescription>Leave empty for ongoing rule</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createRule.isPending || updateRule.isPending}>
                  {editingRule ? 'Update Rule' : 'Create Rule'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Override Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this override rule? This action cannot be undone.
              Any existing job overrides calculated from this rule will remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRuleToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
